import React, { Component, createRef } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { observable } from "mobx";
import { Form, Ref } from "semantic-ui-react";
import TourLayoutWrapper from "../components/TourLayoutWrapper";
import TourNav from "../constants/TourNav";
import TourStore from "~/stores/TourStore";
import CountryStore from "~/stores/CountryStore";
import AddressStore from "~/stores/AddressStore";
import { AddressWithValidation } from "~/components/element/Address/Address";
import Map from "~/components/element/Map";
import SaveButton from "../components/SaveButton";
import ModalUnsavedConfirmation from "~/components/element/ModalUnsavedConfirmation";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import "../styles/TourAddress.less";
import StickyMenu from "~/components/StickyMenu";
import ToastMessageStore from "~/stores/ToastMessageStore";

// The tour lat/lng is rounded to the 6th decimal digit. We must do the same when we get lat/lng from
// the Address (under ~/components/element/Address) and Map components. By doing this we ensure that we can
// compare the tour's coordinates with the Map/Address to see if the form is dirty or not.
const normalizeCoordinate = (value, precision = 6) => {
  const precisionFactor = 10 ** precision;

  return Math.round((value + Number.EPSILON) * precisionFactor) / precisionFactor;
};

@observer
class TourAddress extends Component {
  @observable countryStore = null;

  @observable addressStore = null;

  ref = null;

  constructor(props) {
    super(props);
    document.title = "Tour - Address";
    this.countryStore = new CountryStore();
    this.addressStore = new AddressStore();

    this.ref = createRef();

    this.state = {
      mapWidth: 200,
    };
  }

  componentDidMount() {
    const { tourId, tourStore } = this.props;
    this.countryStore.load();

    // Let's bind the tour.address to the form.
    tourStore.load(tourId).then(() => {
      const { tour } = tourStore;

      this.addressStore.bindTourToForm(tour);
    });

    // calculate container width so we can use it for the map width.
    const mapWidth = this.ref.current.clientWidth;

    this.setState({ mapWidth });
    window.scrollTo(0, 0);
  }

  onSubmit() {
    const { t, tourStore, toastMessageStore } = this.props;
    const { tour } = tourStore;
    const {
      formStore: { model },
    } = this.addressStore;

    return tourStore
      .saveTour(tour.tourId, {
        latitude: model.lat,
        longitude: model.lng,
        address: {
          street: model.street,
          street2: model.street2,
          city: model.city,
          state: model.state,
          country: model.country,
          zip: model.zip,
        },
      })
      .then(() => {
        // bind the updated tour. Note that upper variable "tour" might not reference the updated
        // tourAddress.tour object, for this reason we don't pass it.
        this.addressStore.bindTourToForm(tourStore.tour);
        toastMessageStore.addToastMessage(t("changesSaved"), "positive", t("success"));
      });
  }

  render() {
    const { formStore, selectedAddressResult, addressResults } = this.addressStore;
    const { model, bindInput, setModel, setProperty, isDirty } = formStore;
    const { countries } = this.countryStore;
    const { tourStore, t, tourLayoutContentContainerRef } = this.props;
    const { isSaving, errorMessages } = tourStore;
    const { mapWidth } = this.state;
    const addressServerErrors = errorMessages.tour || {};

    return (
      <>
        <LoadingWrapper loading={isSaving} message={t("saving")} />
        <ModalUnsavedConfirmation when={isDirty} onSave={() => this.onSubmit()} />

        <Ref innerRef={this.ref}>
          <div className="tour-address">
            <StickyMenu
              contextRef={tourLayoutContentContainerRef}
              headerText={t("clientPanelMisc:address")}
              button={<SaveButton disabled={!isDirty} onClick={() => this.onSubmit()} />}
            />

            <Form className="tour-address-form-container" onSubmit={() => this.onSubmit()}>
              <AddressWithValidation
                countries={countries}
                model={model}
                isDirty={isDirty}
                setModel={setModel}
                bindInput={bindInput}
                hasSelectedResult={!!selectedAddressResult}
                addressResults={addressResults}
                onLatLngChange={({ lat, lng }) => {
                  setProperty("lat", normalizeCoordinate(lat));
                  setProperty("lng", normalizeCoordinate(lng));
                }}
                setAddressResults={results => this.addressStore.setAddressResults(results)}
                setSelectedAddressResult={result => this.addressStore.setSelectedAddressResult(result)}
                serverValidationErrors={addressServerErrors}
                useTranslation
              />
            </Form>
            <Map
              width={mapWidth}
              latLng={model.lat || model.lng ? { lat: model.lat, lng: model.lng } : null}
              heightAspectRation={3 / 5}
              onMarkerPositionChange={({ lat, lng }) => {
                setProperty("lat", normalizeCoordinate(lat));
                setProperty("lng", normalizeCoordinate(lng));
              }}
            />
          </div>
        </Ref>
      </>
    );
  }
}

TourAddress.propTypes = {
  tourId: PropTypes.number.isRequired,
  tourStore: PropTypes.instanceOf(TourStore).isRequired,
  t: PropTypes.func.isRequired,
  tourLayoutContentContainerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
};

export default withTranslation()(TourLayoutWrapper(TourAddress, TourNav.ADDRESS));
